/**
 * useLeadPool · the Lead Pool endpoints (sales-lead-pool-cleanup §4.8).
 *
 *   usePoolLeads(params)      GET  /api/v1/leads/pool              — paged rows
 *   usePoolSummary()          GET  /api/v1/leads/pool/summary      — chip counts + rep banner
 *   useSetCustomerType()      PUT  /api/v1/leads/{id}/customer-type
 *   useBulkSetCustomerType()  POST /api/v1/leads/customer-type/bulk
 *
 * Same fetch / error / refetch conventions as `useLeads`. Both readers re-read on any lead write
 * (see `leadBus`), and both writers announce one — setting a type moves rows between the pool
 * and the owner's Pipeline, so every open surface has to hear about it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ApiError, authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { emitLeadChange, onLeadChange } from '../leadBus'
import type {
  ApiResponse,
  BulkSetCustomerTypeResult,
  LeadPoolSummary,
  PaginatedResponse,
  PoolLeadItem,
  PoolLeadsParams,
  SettableCustomerType,
} from '../types'
import type { MutationResult } from './useLeadActions'
import { claimErrorCopy } from '../poolErrors'
import { SUCCESS_TOAST_MS, claimSuccessTitle, settableType } from '../poolDispatch'

interface PaginationState {
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface UsePoolLeadsResult {
  leads: PoolLeadItem[]
  isLoading: boolean
  /** False until the first response (rows or error) has arrived. */
  hasLoaded: boolean
  error: string | null
  pagination: PaginationState
  refetch: () => Promise<void>
}

export function usePoolLeads(params: PoolLeadsParams): UsePoolLeadsResult {
  const [leads, setLeads] = useState<PoolLeadItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    page: params.page ?? 1,
    size: params.size ?? 50,
    totalElements: 0,
    totalPages: 0,
  })
  const token = useAuthStore((s) => s.tokens?.accessToken)
  // Filters change quickly (chips, search); only the newest request may write state, so a slow
  // response for an older query can never overwrite the rows of the current one.
  const requestSeq = useRef(0)

  const fetchLeads = useCallback(async () => {
    const seq = ++requestSeq.current
    const isCurrent = () => seq === requestSeq.current
    if (!token) {
      setError('Not authenticated')
      setIsLoading(false)
      setHasLoaded(true)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams()
      q.append('page', String(params.page ?? 1))
      q.append('size', String(params.size ?? 50))
      if (params.status && params.status !== 'ALL') q.append('status', params.status)
      if (params.source) q.append('source', params.source)
      if (params.ownerId) q.append('ownerId', params.ownerId)
      if (params.createdBy) q.append('createdBy', params.createdBy)
      if (params.engaged) q.append('engaged', 'true')
      if (params.hasDoneBusiness) q.append('hasDoneBusiness', 'true')
      if (params.search) q.append('search', params.search)
      if (params.sort) q.append('sort', params.sort)
      if (params.temperature) q.append('temperature', params.temperature)
      if (params.queue && params.queue !== 'ALL') q.append('queue', params.queue)

      const resp = await authFetch<ApiResponse<PaginatedResponse<PoolLeadItem>>>(
        `/api/v1/leads/pool?${q.toString()}`,
        token,
      )
      if (!isCurrent()) return
      if (resp.success && resp.data) {
        setLeads(resp.data.content)
        setPagination({
          page: resp.data.page,
          size: resp.data.size,
          totalElements: resp.data.totalElements,
          totalPages: resp.data.totalPages,
        })
      } else {
        setError(resp.message ?? 'Failed to load the pool')
        setLeads([])
      }
    } catch (e) {
      if (!isCurrent()) return
      // The backend reports an over-long search as a field validation failure: 400 with
      // details.search = "SEARCH_TOO_LONG: …" (no top-level errorCode).
      const data = e instanceof ApiError ? (e.data as { errorCode?: string; details?: { search?: unknown } } | null) : null
      const searchDetail = typeof data?.details?.search === 'string' ? data.details.search : ''
      const searchTooLong =
        data?.errorCode === 'SEARCH_TOO_LONG' ||
        (e instanceof ApiError && e.status === 400 && searchDetail.startsWith('SEARCH_TOO_LONG'))
      setError(
        searchTooLong
          ? 'Search is too long'
          : e instanceof Error
            ? e.message
            : 'Failed to load the pool',
      )
      setLeads([])
    } finally {
      if (isCurrent()) {
        setIsLoading(false)
        setHasLoaded(true)
      }
    }
  }, [token, params.page, params.size, params.status, params.source, params.ownerId, params.createdBy, params.engaged, params.hasDoneBusiness, params.search, params.sort, params.temperature, params.queue])

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  useEffect(() => onLeadChange(fetchLeads), [fetchLeads])

  return { leads, isLoading, hasLoaded, error, pagination, refetch: fetchLeads }
}

/** Seniority designations treated as managers in the pool (see LeadPoolPage). */
export const MANAGERIAL_ROLES = new Set(['ADMIN', 'MANAGER'])

/**
 * Manager/admin by seniority designation, not by permission role — a BDE team lead is role
 * MANAGER with roleName "BDE", and it is the designation the backend gates on.
 */
export function useIsSalesManager(): boolean {
  const role = useAuthStore((s) => s.user?.role)
  return MANAGERIAL_ROLES.has((role ?? '').toUpperCase())
}

export interface UsePoolSummaryResult {
  summary: LeadPoolSummary | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * [ownerId]: the Backlog tab's owner filter. The chip counts follow it, the way the list below does
 * (the rep banner count `provisionalOwnedByMe` never changes with it).
 */
export function usePoolSummary(ownerId?: string): UsePoolSummaryResult {
  const [summary, setSummary] = useState<LeadPoolSummary | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const fetchSummary = useCallback(async () => {
    if (!token) return
    setIsLoading(true)
    setError(null)
    try {
      const qs = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : ''
      const resp = await authFetch<ApiResponse<LeadPoolSummary>>(`/api/v1/leads/pool/summary${qs}`, token)
      if (resp.success && resp.data) setSummary(resp.data)
      else setError(resp.message ?? 'Failed to load pool counts')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pool counts')
    } finally {
      setIsLoading(false)
    }
  }, [token, ownerId])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => onLeadChange(fetchSummary), [fetchSummary])

  return { summary, isLoading, error, refetch: fetchSummary }
}

/** Shared failure mapping: the server's message and errorCode, never a guessed one. */
function toFailure<T>(e: unknown, fallback: string): MutationResult<T> {
  const errorCode = e instanceof ApiError ? (e.data as { errorCode?: string } | null)?.errorCode : undefined
  const status = e instanceof ApiError ? e.status : undefined
  return { success: false, message: e instanceof Error ? e.message : fallback, errorCode, status }
}

export function useSetCustomerType() {
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const setCustomerType = useCallback(
    async (leadId: string, type: SettableCustomerType): Promise<MutationResult<PoolLeadItem>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<ApiResponse<PoolLeadItem>>(
          `/api/v1/leads/${encodeURIComponent(leadId)}/customer-type`,
          token,
          { method: 'PUT', body: JSON.stringify({ type }), suppressPermissionToast: true },
        )
        if (resp.success) {
          emitLeadChange()
          return { success: true, data: resp.data, message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toFailure(e, 'Could not set the customer type')
      }
    },
    [token],
  )

  return { setCustomerType }
}

export function useBulkSetCustomerType() {
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const bulkSetCustomerType = useCallback(
    async (
      leadIds: string[],
      type: SettableCustomerType,
    ): Promise<MutationResult<BulkSetCustomerTypeResult>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<ApiResponse<BulkSetCustomerTypeResult>>(
          '/api/v1/leads/customer-type/bulk',
          token,
          { method: 'POST', body: JSON.stringify({ leadIds, type }), suppressPermissionToast: true },
        )
        if (resp.success && resp.data) {
          // Partial success is still a write — the rows that did change have moved.
          if (resp.data.updated.length > 0) emitLeadChange()
          return { success: true, data: resp.data, message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toFailure(e, 'Could not set the customer type')
      }
    },
    [token],
  )

  return { bulkSetCustomerType }
}

/** A refusal with the error body's `details` kept (HOLDING_CAP_REACHED carries its counts there). */
export interface PoolRefusal<T> extends MutationResult<T> {
  details?: unknown
}

/** Shared failure mapping that also keeps `details`, for the dispatch endpoints. */
export function toPoolRefusal<T>(e: unknown, fallback: string): PoolRefusal<T> {
  const body = e instanceof ApiError ? (e.data as { errorCode?: string; details?: unknown } | null) : null
  return {
    success: false,
    message: e instanceof Error ? e.message : fallback,
    errorCode: body?.errorCode,
    status: e instanceof ApiError ? e.status : undefined,
    details: body?.details ?? body ?? undefined,
  }
}

export interface PoolClaimOptions {
  /**
   * Claim-as-type (dispatch L2): for an untyped company the server sets this type, then claims,
   * in one request. Omitted = a plain claim (no body), exactly as before.
   */
  customerType?: SettableCustomerType
  /** Toast action on success — e.g. "Open" the claimed lead's drawer. */
  onOpen?: () => void
}

/**
 * Claim with the pool's toasts, shared by the pool row, the live board and the lead drawer.
 *
 * In-flight ids live in a ref (synchronous double-submit guard) mirrored into state (spinners), so
 * finishing one claim never clears another row's spinner.
 */
export function usePoolClaim() {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const inFlight = useRef(new Set<string>())
  const [claimingIds, setClaimingIds] = useState<ReadonlySet<string>>(() => new Set())

  const claim = useCallback(
    async (leadId: string, name: string, opts: PoolClaimOptions = {}): Promise<boolean> => {
      if (inFlight.current.has(leadId)) return false
      if (!token) {
        toast.error('Not authenticated')
        return false
      }
      inFlight.current.add(leadId)
      setClaimingIds(new Set(inFlight.current))
      let res: PoolRefusal<PoolLeadItem>
      try {
        const resp = await authFetch<ApiResponse<PoolLeadItem>>(
          `/api/v1/leads/${encodeURIComponent(leadId)}/claim`,
          token,
          {
            method: 'POST',
            ...(opts.customerType ? { body: JSON.stringify({ customerType: opts.customerType }) } : {}),
            // The pool explains a 403 in its own toast (see poolErrors); the generic
            // permission-denied toast on top of it would say the same thing twice.
            suppressPermissionToast: true,
          },
        )
        res = resp.success
          ? { success: true, data: resp.data, message: resp.message }
          : { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        res = toPoolRefusal(e, 'Could not claim this opportunity')
      }
      inFlight.current.delete(leadId)
      setClaimingIds(new Set(inFlight.current))
      if (res.success) {
        // The type the server actually holds — it ignores a requested type for a company that was
        // already typed, so the request is not the truth. A plain claim never names one.
        const appliedType = opts.customerType ? settableType(res.data?.customerType) : undefined
        toast.success(claimSuccessTitle(name, appliedType), {
          description: 'It has moved into your pipeline.',
          duration: SUCCESS_TOAST_MS,
          action: opts.onOpen ? { label: 'Open', onClick: opts.onOpen } : undefined,
        })
      } else {
        const copy = claimErrorCopy(res)
        toast.error(copy.title, { description: copy.description })
      }
      // Pool rows, chip counts, an open drawer and any Pipeline all re-read.
      emitLeadChange()
      return res.success
    },
    [token],
  )

  return { claim, claimingIds }
}
